import React, { useState } from 'react';
import { X, Eye, EyeOff, ExternalLink, CheckCircle, ShieldCheck } from 'lucide-react';
import { useSlotAssetGeneratorStore } from '../../store/slotAssetGeneratorStore';
import type { AIProviderKey } from '../../../core/types/image-generation.types';
import { hasEnvApiKey } from '../../utils/api-keys';
import { cn } from '../../utils/cn';
import { shouldUseProxy } from '../../services/ai-proxy';

interface ProviderConfig {
  key: AIProviderKey;
  name: string;
  description: string;
  models: string[];
  docsUrl: string;
  placeholder: string;
}

const PROVIDERS: ProviderConfig[] = [
  {
    key: 'google',
    name: 'Google AI',
    description: 'Gemini image generation via Generative Language API',
    models: ['Soul 2.0', 'Nano Banana 2'],
    docsUrl: 'https://aistudio.google.com/apikey',
    placeholder: 'AIza...',
  },
  {
    key: 'openai',
    name: 'OpenAI',
    description: 'DALL-E image generation',
    models: ['GPT Image 1.5'],
    docsUrl: 'https://platform.openai.com/api-keys',
    placeholder: 'sk-...',
  },
  {
    key: 'huggingface',
    name: 'Hugging Face',
    description: 'Stable Diffusion and FLUX image generation',
    models: ['Seedream 5.0', 'Flux Kontext'],
    docsUrl: 'https://huggingface.co/settings/tokens',
    placeholder: 'hf_...',
  },
];

export const ApiKeySettings: React.FC = () => {
  const apiKeys = useSlotAssetGeneratorStore((s) => s.apiKeys);
  const setApiKey = useSlotAssetGeneratorStore((s) => s.setApiKey);
  const isSettingsOpen = useSlotAssetGeneratorStore((s) => s.isSettingsOpen);
  const toggleSettings = useSlotAssetGeneratorStore((s) => s.toggleSettings);
  const [visibleKeys, setVisibleKeys] = useState<Set<AIProviderKey>>(new Set());

  if (!isSettingsOpen) return null;

  const toggleVisibility = (provider: AIProviderKey) => {
    setVisibleKeys((prev) => {
      const next = new Set(prev);
      if (next.has(provider)) next.delete(provider);
      else next.add(provider);
      return next;
    });
  };

  return (
    <div className="fixed inset-0 z-[1000] flex items-center justify-center bg-black/50" onClick={toggleSettings}>
      <div
        className="bg-zinc-900 border border-zinc-700 rounded-xl shadow-2xl w-[520px] max-h-[80vh] flex flex-col"
        onClick={(e) => e.stopPropagation()}
      >
        {/* Header */}
        <div className="flex items-center justify-between px-5 py-4 border-b border-zinc-800">
          <div>
            <h2 className="text-sm font-semibold text-zinc-200">API Key Settings</h2>
            <p className="text-xs text-zinc-500 mt-0.5">Configure API keys for image generation providers</p>
          </div>
          <button
            onClick={toggleSettings}
            className="p-1.5 rounded-md text-zinc-500 hover:bg-zinc-800 hover:text-zinc-300 transition-colors"
          >
            <X size={16} />
          </button>
        </div>

        {/* Provider List */}
        <div className="flex-1 overflow-y-auto px-5 py-4 space-y-4">
          {PROVIDERS.map((provider) => {
            const currentKey = apiKeys[provider.key] ?? '';
            const isVisible = visibleKeys.has(provider.key);
            const envAvailable = hasEnvApiKey(provider.key);
            const proxyActive = provider.key === 'google' && !currentKey && shouldUseProxy();
            const hasKey = currentKey.length > 0 || envAvailable || proxyActive;

            return (
              <div key={provider.key} className="space-y-2">
                <div className="flex items-center justify-between">
                  <div className="flex items-center gap-2">
                    <span className="text-sm font-medium text-zinc-200">{provider.name}</span>
                    {hasKey && <CheckCircle size={12} className="text-green-400" />}
                    {proxyActive && (
                      <span className="flex items-center gap-1 text-[9px] px-1.5 py-0.5 rounded bg-green-500/20 text-green-400 font-medium">
                        <ShieldCheck size={10} /> Secure proxy
                      </span>
                    )}
                    {envAvailable && !currentKey && !proxyActive && (
                      <span className="text-[9px] px-1.5 py-0.5 rounded bg-blue-500/20 text-blue-400 font-medium">ENV</span>
                    )}
                  </div>
                  <a
                    href={provider.docsUrl}
                    target="_blank"
                    rel="noopener noreferrer"
                    className="flex items-center gap-1 text-[10px] text-zinc-500 hover:text-blue-400 transition-colors"
                    onClick={(e) => e.stopPropagation()}
                  >
                    Get API key <ExternalLink size={10} />
                  </a>
                </div>

                <p className="text-xs text-zinc-500">
                  {provider.description}
                  {proxyActive && (
                    <span className="text-green-400/70"> — API key secured on server</span>
                  )}
                </p>

                <div className="flex items-center gap-2">
                  <div className="relative flex-1">
                    <input
                      type={isVisible ? 'text' : 'password'}
                      value={currentKey}
                      onChange={(e) => setApiKey(provider.key, e.target.value)}
                      placeholder={
                        proxyActive
                          ? 'Using secure server proxy (enter key to override)'
                          : envAvailable && !currentKey
                            ? 'Using environment key (enter to override)'
                            : provider.placeholder
                      }
                      className="w-full bg-zinc-800 border border-zinc-700 rounded-md px-3 py-1.5 pr-9 text-xs text-zinc-200 placeholder-zinc-600 focus:outline-none focus:border-zinc-600 font-mono"
                    />
                    <button
                      onClick={() => toggleVisibility(provider.key)}
                      className="absolute right-2 top-1/2 -translate-y-1/2 text-zinc-500 hover:text-zinc-300 transition-colors"
                    >
                      {isVisible ? <EyeOff size={14} /> : <Eye size={14} />}
                    </button>
                  </div>
                </div>

                <div className="flex flex-wrap gap-1">
                  {provider.models.map((model) => (
                    <span
                      key={model}
                      className={cn(
                        'text-[10px] px-1.5 py-0.5 rounded-full',
                        currentKey.length > 0
                          ? 'bg-green-500/10 text-green-400'
                          : proxyActive
                            ? 'bg-green-500/10 text-green-400'
                            : envAvailable
                              ? 'bg-blue-500/10 text-blue-400'
                              : 'bg-zinc-800 text-zinc-500'
                      )}
                    >
                      {model}
                    </span>
                  ))}
                </div>
              </div>
            );
          })}
        </div>

        {/* Footer */}
        <div className="px-5 py-3 border-t border-zinc-800">
          <p className="text-[10px] text-zinc-600">
            Keys are stored locally in your browser. Models without an API key (Pollinations) work for free.
            {' '}<span className="text-blue-500/70">ENV</span> = key provided by environment variable.
            {' '}<span className="text-green-500/70">Secure proxy</span> = API key held server-side (recommended).
          </p>
        </div>
      </div>
    </div>
  );
};
